/**
 * Bookkeeping for building one track's runs on demand.
 *
 * The review player turns each run of chunks into a single playable blob, and
 * it does that lazily — only the run being watched, plus the one after it, so
 * opening an attempt does not download every megabyte of three devices before
 * the first frame. That means several builds can be in flight, arriving in any
 * order, while the manifest underneath them may be replaced (a refetch after an
 * intervention, an expired set of signed URLs).
 *
 * Two things have to hold, and getting either wrong is invisible until someone
 * is staring at a spinner:
 *
 *   - **A run is built once.** Two renders wanting the same run must not fetch
 *     it twice.
 *   - **A claim is always released unless it produced something.** This is the
 *     one that bit: a build discarded for being from a superseded manifest
 *     returned early and left its run marked "already being built". Nothing was
 *     building it, and nothing ever would — the pane read "Loading footage…"
 *     for the rest of the session.
 *
 * The generation is what ties a build to the manifest it was started for, and
 * it is captured ALONGSIDE the claim set, so the claim is released from the
 * same set it was made on rather than from whatever set is current when the
 * fetch happens to finish.
 */
export class RunLoader {
  private gen = 0;
  private claimed = new Set<number>();

  /** The manifest generation builds must quote to be accepted. */
  get generation(): number {
    return this.gen;
  }

  /** A new manifest: abandon everything in flight and drop every claim. */
  reset(): void {
    this.gen += 1;
    this.claimed = new Set();
  }

  /**
   * Runs `work` for run `i` unless it is already built or being built.
   *
   * Resolves to `null` when nothing was produced — already claimed, or the
   * generation has moved on — so the caller can tell "here is the blob" from
   * "not mine to apply".
   */
  async build<T>(
    gen: number,
    i: number,
    work: () => Promise<T>,
  ): Promise<T | null> {
    if (gen !== this.gen) return null;

    // Captured together: if a reset lands mid-flight, the release below must
    // touch the set this claim was made on, never the live one.
    const claimed = this.claimed;
    if (claimed.has(i)) return null;
    claimed.add(i);

    try {
      const value = await work();
      // Superseded: this build was started against a manifest that has since
      // been replaced, so its bytes are not ours to apply.
      //
      // Nothing is released here, and that is not an oversight. Reaching this
      // branch means a reset happened during the await, and a reset replaces
      // the claim set — so `claimed` is provably the superseded one and the
      // live set never held this run. Releasing from the LIVE set instead is
      // the mistake: it frees a claim the new manifest is legitimately
      // building, and the run is fetched twice.
      if (gen !== this.gen) return null;
      return value;
    } catch (err) {
      // Released, so scrubbing back here tries again rather than showing a
      // permanent failure for what may have been one dropped request.
      claimed.delete(i);
      throw err;
    }
  }
}
